import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Button, Center, Divider, Flex, Link } from '@chakra-ui/react';

import { Lead } from '@backend/src/frontend-mongo-interfaces/mongoose.gen';
import { fireGtmEvent } from 'actions/appActions';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { selectLeadFinishValues } from 'slices/leadSlice';
import UlfStepContainer, { UlfStepInner } from 'components/UlfStepContainer';
import Intro from 'components/Intro';
import Loading from 'components/Loading';
import StartAnother from 'components/StartAnother';

const SkipRooms = () => {
  const { t } = useTranslation();
  return (
    <>
      <Intro
        title={<Trans>Talk to you soon!</Trans>}
        rawDescription={
          <Trans>
            Thank you! We'll reach out shortly to follow up about the services
            you selected and to gather additional details.
          </Trans>
        }
      />
      <Box maxW="md" mx="auto" mb={10}>
        <picture>
          <source
            srcSet="/img/tmt-finish-offline-dark.gif"
            media="(prefers-reduced-motion: no-preference) and (prefers-color-scheme: dark)"
          />
          <source
            srcSet="/img/tmt-finish-offline-dark.png"
            media="(prefers-color-scheme: dark)"
          />
          <source
            srcSet="/img/tmt-finish-offline.gif"
            media="(prefers-reduced-motion: no-preference)"
          />
          <img
            srcSet="/img/tmt-finish-offline.png"
            alt={t("You've completed your moving form!")}
          />
        </picture>
      </Box>
    </>
  );
};

const LoadingStatus = () => {
  return (
    <>
      <Intro
        title={<Trans>Processing</Trans>}
        rawDescription={
          <>
            <Trans>Please wait a moment while we submit your estimate.</Trans>
            <Center>
              <Loading />
            </Center>
          </>
        }
      />
    </>
  );
};

const NoEstimateDetails = () => {
  const { t } = useTranslation();
  return (
    <>
      <Intro
        title={<Trans>We'll get moving on your quote!</Trans>}
        rawDescription={
          <Trans>
            We've got all the information we need to get working on your quote
            and will contact you once it's ready.
          </Trans>
        }
      />
      <Box maxW="md" mx="auto" mb={10}>
        <picture>
          <source
            srcSet="/img/tmt-finish-offline-dark.gif"
            media="(prefers-reduced-motion: no-preference) and (prefers-color-scheme: dark)"
          />
          <source
            srcSet="/img/tmt-finish-offline-dark.png"
            media="(prefers-color-scheme: dark)"
          />
          <source
            srcSet="/img/tmt-finish-offline.gif"
            media="(prefers-reduced-motion: no-preference)"
          />
          <img
            srcSet="/img/tmt-finish-offline.png"
            alt={t("You've completed your moving form!")}
          />
        </picture>
      </Box>
    </>
  );
};

const EstimateDetails = ({ link }: { link: string }) => {
  const { t } = useTranslation();
  return (
    <>
      <Intro
        title={<Trans>Your estimate is ready!</Trans>}
        rawDescription={
          <Flex direction="column" align="center" gap={4} mt={4}>
            <Box maxW="sm" mx="auto" mb={4}>
              <picture>
                <source
                  srcSet="/img/estimate-ready-dark-2.png"
                  media="(prefers-color-scheme: dark)"
                />
                <img
                  srcSet="/img/estimate-ready-2.png"
                  alt={t("You've completed your moving form!")}
                />
              </picture>
            </Box>
            <Button as={Link} href={`http://${link}`} size="lg">
              <Trans>View estimate now</Trans>
            </Button>
          </Flex>
        }
      />
    </>
  );
};

export const FinishForm = ({
  roomsStart,
  estimateID,
  estimateCanGetPreviewLink,
}: Pick<Lead, 'roomsStart' | 'estimateID' | 'estimateCanGetPreviewLink'>) => {
  const isSubmitting = !estimateID;
  return (
    <UlfStepContainer>
      <UlfStepInner>
        {!roomsStart && <SkipRooms />}
        {roomsStart && isSubmitting && <LoadingStatus />}
        {roomsStart &&
          !isSubmitting &&
          (!estimateCanGetPreviewLink ||
            estimateCanGetPreviewLink.length === 0) && <NoEstimateDetails />}
        {roomsStart &&
          !isSubmitting &&
          !!estimateCanGetPreviewLink &&
          estimateCanGetPreviewLink.length > 0 && (
            <EstimateDetails link={estimateCanGetPreviewLink} />
          )}
        <Divider my={6} />
        <StartAnother />
      </UlfStepInner>
    </UlfStepContainer>
  );
};

const Finish = () => {
  const lead = useAppSelector(selectLeadFinishValues);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fireGtmEvent('leadComplete'));
  }, [dispatch]);

  return <FinishForm {...lead} />;
};

export default Finish;
